import * as Sentry from '@sentry/vue';

import {HTTP_200_OK, HTTP_202_ACCEPTED} from '@/api';
import facebookApi from '@/facebookApi';

export const CONNECT_FACEBOOK_TO_INSTAGRAM_HELP_PAGE_URL =
  'https://www.facebook.com/business/help/898752960195806';

/**
 * Functionality to share something via social media (Facebook, Instagram).
 *
 * This mixin is meant to be used on the parent component that hosts the
 * ShareOnFacebookPageDialog component.
 *
 * Depends on:
 *   - this.bundle
 *   - this.spot
 */
export default {
  data() {
    return {
      // Choose platform and tvspot.
      targetSocialMediaPlatform: 'facebook', // One of: 'facebook', 'instagram'.
      showChooseSpotToShareOnSocialMediaDialog: false,
      chosenSpotToShareOnSocialMedia: null,

      graphApiUserAccessToken: null,
      busyTalkingToGraphApi: false,

      // Facebook
      sharingToFacebookPagesProgress: null,
      showShareOnFacebookDialog: false,
      userManagedFacebookPages: [],

      // Instagram
      showShareOnInstagramDialog: false,
    };
  },
  computed: {
    userManagedFacebookPagesWithConnectedInstagram() {
      if (!this.userManagedFacebookPages) {
        return [];
      }
      return this.userManagedFacebookPages.filter((page) => {
        return (
          !!page.instagram_business_account &&
          !!page.instagram_business_account.id
        );
      });
    },
    /**
     * TVSpot can be shared to Facebook when it:
     *   - has an absolute_shared_url
     *   - has a specific resolution (1920x1080 or 1080x1920 or 1080x1080)
     *
     * It also has to be processed, as sharing non existing videos makes no sense.
     */
    spotsToShareOnFacebook() {
      if (!this.bundle || !this.bundle.tvspots) {
        return [];
      }
      return this.bundle.tvspots.filter((tvspot) => {
        return (
          tvspot.processed &&
          !!tvspot.absolute_shared_url &&
          ((tvspot.tvformat.width === 1920 &&
            tvspot.tvformat.height === 1080) ||
            (tvspot.tvformat.width === 1080 &&
              tvspot.tvformat.height === 1920) ||
            (tvspot.tvformat.width === 1080 && tvspot.tvformat.height === 1080))
        );
      });
    },
    /**
     * TVSpot can be shared to Instagram when it:
     *   - has an absolute_shared_url
     *   - has a specific resolution (1920x1080 or 1080x1080)
     *
     * Also see: https://developers.facebook.com/docs/instagram-api/reference/ig-user/media#video-specifications
     *
     * It also has to be processed, as sharing non existing videos makes no sense.
     */
    spotsToShareOnInstagram() {
      if (!this.bundle || !this.bundle.tvspots) {
        return [];
      }
      return this.bundle.tvspots.filter((tvspot) => {
        return (
          tvspot.processed &&
          tvspot.duration >= 3 &&
          tvspot.duration <= 60 &&
          !!tvspot.absolute_shared_url &&
          ((tvspot.tvformat.width === 1920 &&
            tvspot.tvformat.height === 1080) ||
            (tvspot.tvformat.width === 1080 && tvspot.tvformat.height === 1080))
        );
      });
    },
    atLeastOneShareableFacebookSpotAvailable() {
      return this.spotsToShareOnFacebook.length > 0;
    },
    atLeastOneShareableInstagramSpotAvailable() {
      return this.spotsToShareOnInstagram.length > 0;
    },
    atLeastOneShareableSpotAvailable() {
      return (
        this.atLeastOneShareableFacebookSpotAvailable ||
        this.atLeastOneShareableInstagramSpotAvailable
      );
    },
    showShareOnSocialMediaButton() {
      return this.atLeastOneShareableSpotAvailable;
    },
    /**
     * Sharing an editable HTML-Spot is currently limited to certain Plans.
     */
    enableShareOnFacebookButton() {
      if (!this.spotsToShareOnFacebook.length) {
        return false;
      }
      const tvspot = this.spotsToShareOnFacebook[0];
      if (tvspot.editable_template) {
        // return (
        //   this.$userprofile &&
        //   this.$userprofile.content_creator_plan &&
        //   this.$userprofile.content_creator_plan
        //     .can_share_produced_content_to_facebook
        // );

        // for now, sharing of editable clips is enabled within demo / beta
        return true;
      }
      return true;
    },
    enableShareOnInstagramButton() {
      return this.atLeastOneShareableInstagramSpotAvailable;
    },
  },
  methods: {
    /**
     * Pass chosen TVSpot to the handler for the chosen social media platform.
     */
    onSpotToShareOnTargetSocialMediaPlatformChosen(tvspot) {
      this.chosenSpotToShareOnSocialMedia = tvspot;

      if (this.targetSocialMediaPlatform === 'facebook') {
        this.onShareSpotOnFacebookPageInitiated();
      } else if (this.targetSocialMediaPlatform === 'instagram') {
        this.onShareSpotOnInstagramInitiated();
      }
    },
    /**
     * Instagram allows share-to-feed only within native apps.
     */
    async onShareSpotOnInstagramInitiated() {
      await this.fetchFacebookManagedPagesShowErrorOnFailure();

      // Check at least one is connected to an Instagram business account.
      const instagramConnectedPages = this.userManagedFacebookPages.filter(
        (page) =>
          !!page.instagram_business_account &&
          !!page.instagram_business_account.id
      );
      if (instagramConnectedPages.length === 0) {
        this.$bus.$emit('alert-warning', {
          message:
            this.$t('noManagedFacebookPagesConnectedToInstagramError') + ':',
          translate: false,
          timeout: 0,
          link: {
            text: this.$t('connectInstagramToFacebookHelpPageLabel'),
            path: CONNECT_FACEBOOK_TO_INSTAGRAM_HELP_PAGE_URL,
            external: true,
          },
        });
        return;
      }

      this.showShareOnInstagramDialog = true;
    },
    chooseSpotToShareOnFacebook() {
      this.targetSocialMediaPlatform = 'facebook';
      this.showChooseSpotToShareOnSocialMediaDialog = true;
    },
    chooseSpotToShareOnInstagram() {
      this.targetSocialMediaPlatform = 'instagram';
      this.showChooseSpotToShareOnSocialMediaDialog = true;
    },
    async fetchFacebookManagedPagesShowErrorOnFailure() {
      let managedPagesErrorMessage = null;

      // Retrieving the token ensures we are logged in and can use the SDK.
      // If we gave none or incomplete permissions, this may fail.
      this.busyTalkingToGraphApi = true;
      try {
        this.graphApiUserAccessToken =
          await facebookApi.getFacebookUserAccessToken();

        try {
          this.userManagedFacebookPages =
            await facebookApi.getFacebookUserManagedPages();
        } catch (error) {
          managedPagesErrorMessage = error;
        }
      } catch (error) {
        managedPagesErrorMessage = error;
        facebookApi.logoutFacebook();
      } finally {
        this.busyTalkingToGraphApi = false;
      }

      if (!this.userManagedFacebookPages.length || managedPagesErrorMessage) {
        // Something went wrong.
        this.$bus.$emit('alert-warning', {
          message: 'noManagedFacebookPagesError',
          translate: true,
          timeout: 0,
        });
        if (managedPagesErrorMessage) {
          this.$bus.$emit('alert-error', {
            message: managedPagesErrorMessage,
            translate: false,
            timeout: 0,
          });
        }
        // Logout so if a page is added we have a change to recognize
        // that, as otherwise looking at the cached login-status won't
        // send a new request as long as the auth is still valid.
        facebookApi.logoutFacebook();
        return;
      }
    },
    async onShareSpotOnFacebookPageInitiated() {
      await this.fetchFacebookManagedPagesShowErrorOnFailure();
      if (!this.userManagedFacebookPages.length) {
        return;
      }
      this.showShareOnFacebookDialog = true;
    },
    /**
     * Share a video to one or more Facebook pages.
     *
     * Args:
     *
     *   chosenPagesPayload (Array[Object]): A list of objects like:
     *     - pageAccessToken (String)
     *     - pageId (Number)
     *     - pageLink (String)
     *     - pageName (String)
     *     - description (String)
     *     - overlayData (String): Optional
     *     - scheduledPublishTime (Number): Optional (UNIX Epoch)
     *
     * Individual failed page requests are shown as error alerts. We do
     * show one final success alert when all request succeeded: It's
     * text differs between sharing 'normal' TVSpots and 'editable'
     * HTML-Spots. Some data is sent to Sentry for debugging.
     *
     * Returns:
     *
     *   A combined Promise based on the individual page responses.
     */
    onShareSpotOnFacebookConfirmed(chosenPagesPayload) {
      const promises = [];

      // Start with a small progress value to indicate we are busy, then
      // update with every page as we go.
      const progressPerPage = 100 / chosenPagesPayload.length;
      const initialProgress = progressPerPage / 3.0;

      this.sharingToFacebookPagesProgress = initialProgress;
      this.busyTalkingToGraphApi = true;

      const vm = this;

      function handlePageResponse(response, facebookPayload) {
        console.log(
          '[DEBUG] FB share to page response:',
          response,
          facebookPayload
        );

        // Update progress.
        vm.sharingToFacebookPagesProgress += progressPerPage;
        if (vm.sharingToFacebookPagesProgress > 100) {
          vm.sharingToFacebookPagesProgress = 100.0;
        }

        // Either we shared to Facebook and it gave us a status payload
        // (200), or the sharing has been delayed on Celery (202).
        const successful =
          response.status == HTTP_202_ACCEPTED ||
          response.data.status == HTTP_200_OK;

        if (successful) {
          Sentry.captureMessage('Shared on facebook', {
            extra: {
              response: JSON.stringify({
                response: response,
                facebookPayload: facebookPayload,
              }),
            },
            level: 'info',
          });
        } else {
          console.error(response.data);

          vm.$bus.$emit('alert-error', {
            message: response.data.message,
            translate: false,
            timeout: 0,
          });

          Sentry.captureMessage('Failed to share on facebook', {
            extra: {
              response: JSON.stringify({
                response: response,
                facebookPayload: facebookPayload,
              }),
            },
            level: 'error',
          });
        }
      }

      function handlePageError(error, facebookPayload) {
        console.error(
          '[ERROR] FB share to page error:',
          error,
          facebookPayload
        );

        vm.$bus.$emit('alert-error', {
          message: 'genericFailFeedback',
          translate: true,
          timeout: 8000,
        });

        Sentry.captureMessage('Failed to share on facebook', {
          extra: {
            response: JSON.stringify({
              response: error,
              facebookPayload: facebookPayload,
            }),
          },
          level: 'error',
        });
      }

      for (let payload of chosenPagesPayload) {
        const facebookPayload = {
          description: payload.description,
          pageAccessToken: payload.pageAccessToken,
          pageId: payload.pageId,
          pageLink: payload.pageLink,
          pageName: payload.pageName,
        };
        if (payload.overlayData) {
          facebookPayload.overlayData = payload.overlayData;
        }
        if (payload.scheduledPublishTime) {
          facebookPayload.scheduledPublishTime = payload.scheduledPublishTime;
        }

        const promise = this.$api.shareVideoOnFacebookPage(
          payload.tvspotId,
          facebookPayload
        );
        promise
          .then((response) => handlePageResponse(response, facebookPayload))
          .catch((error) => handlePageError(error, facebookPayload));
        promises.push(promise);
      }

      const combinedPromise = Promise.all(promises);
      combinedPromise
        .then((responses) => {
          const statusCodes = responses.map((response) => response.status);

          const facebookStatusCodes = responses
            .filter((response) => response.data && response.data.status)
            .map((response) => response.data.status);

          const allStatusCodes = statusCodes.concat(facebookStatusCodes);

          const allSuccessful =
            allStatusCodes.filter(
              (code) => code == HTTP_200_OK || code == HTTP_202_ACCEPTED
            ).length === allStatusCodes.length;

          const someDelayed =
            statusCodes.filter((code) => code == HTTP_202_ACCEPTED).length > 0;

          if (allSuccessful) {
            const successMessageKey = someDelayed
              ? 'shareOnFacebookPageSuccessDelayed'
              : 'shareOnFacebookPageSuccess';

            this.$bus.$emit('alert-success', {
              message: successMessageKey,
              translate: true,
              timeout: 15000,
            });
            this.sharingToFacebookPagesProgress = null;
            this.showShareOnFacebookDialog = false;
          }
        })
        .finally(() => {
          this.busyTalkingToGraphApi = false;
        });
      return combinedPromise;
    },
    /**
     * Share a video to an Instagram-connected Facebook page.
     *
     * Args:
     *
     *   chosenPagePayload (Object):
     *     - pageAccessToken (String)
     *     - pageId (Number)
     *     - caption (String): Optional
     *     - overlayData (String): Optional
     *
     * Returns:
     *
     *   Promises
     */
    onShareSpotOnInstagramConfirmed(chosenPagePayload) {
      this.busyTalkingToGraphApi = true;

      const vm = this;

      function handlePageResponse(response, instagramPayload) {
        console.log(
          '[DEBUG] Instagram share response:',
          response,
          instagramPayload
        );

        // Either we shared to Facebook and it gave us a status payload
        // (200), or the sharing has been delayed on Celery (202).
        const successful =
          response.status == HTTP_202_ACCEPTED ||
          response.data.status == HTTP_200_OK;

        if (successful) {
          Sentry.captureMessage('Shared on Instagram', {
            extra: {
              response: JSON.stringify({
                response: response,
                instagramPayload: instagramPayload,
              }),
            },
            level: 'info',
          });
        } else {
          console.error(response.data);

          vm.$bus.$emit('alert-error', {
            message: response.data.message,
            translate: true,
            timeout: 0,
          });

          Sentry.captureMessage('Failed to share on Instagram', {
            extra: {
              response: JSON.stringify(
                {response: response, instagramPayload: instagramPayload},
                null,
                2
              ),
            },
            level: 'error',
          });
        }
      }

      function handlePageError(error, instagramPayload) {
        console.error(
          '[ERROR] Instagram share error:',
          error,
          instagramPayload
        );

        vm.$bus.$emit('alert-error', {
          message: 'genericFailFeedback',
          translate: true,
          timeout: 8000,
        });

        Sentry.captureMessage('Failed to share on Instagram', {
          extra: {
            response: JSON.stringify({
              response: error,
              instagramPayload: instagramPayload,
            }),
          },
          level: 'error',
        });
      }

      const instagramPayload = {
        caption: chosenPagePayload.caption,
        instagramBusinessAccountId:
          chosenPagePayload.instagramBusinessAccountId,
        pageId: chosenPagePayload.pageId,
        userAccessToken: this.graphApiUserAccessToken,
      };
      if (chosenPagePayload.overlayData) {
        instagramPayload.overlayData = chosenPagePayload.overlayData;
      }

      const promise = this.$api.shareVideoOnInstagram(
        chosenPagePayload.tvspotId,
        instagramPayload
      );
      promise
        .then((response) => handlePageResponse(response, instagramPayload))
        .catch((error) => handlePageError(error, instagramPayload));

      const promises = [promise];

      const combinedPromise = Promise.all(promises);
      combinedPromise
        .then((responses) => {
          const statusCodes = responses.map((response) => response.status);

          const facebookStatusCodes = responses
            .filter((response) => response.data && response.data.status)
            .map((response) => response.data.status);

          const allStatusCodes = statusCodes.concat(facebookStatusCodes);

          const allSuccessful =
            allStatusCodes.filter(
              (code) => code == HTTP_200_OK || code == HTTP_202_ACCEPTED
            ).length === allStatusCodes.length;

          const someDelayed =
            statusCodes.filter((code) => code == HTTP_202_ACCEPTED).length > 0;

          if (allSuccessful) {
            const successMessageKey = someDelayed
              ? 'shareOnInstagramSuccessDelayed'
              : 'shareOnInstagramSuccess';

            this.$bus.$emit('alert-success', {
              message: successMessageKey,
              translate: true,
              timeout: 15000,
            });
            this.showShareOnInstagramDialog = false;
          }
        })
        .finally(() => {
          this.busyTalkingToGraphApi = false;
        });
      return combinedPromise;
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/shareOnSocialMedia.js